import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ForceChangePasswordPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) { setError("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      await refresh();
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <form onSubmit={submit} className="w-full max-w-md p-6 rounded-lg border border-amber-700/50 bg-slate-900/60" data-testid="force-change-form">
        <div className="flex items-center gap-2 mb-2 text-amber-400">
          <KeyRound className="h-5 w-5" />
          <span className="text-[10px] uppercase tracking-widest font-mono-num">Required</span>
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">You must set a new password</h1>
        <p className="text-sm text-slate-400 mb-5">An administrator reset your password. Set a new one to continue.</p>
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Current (temporary) password</label>
        <PasswordInput testId="force-current-password" required value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">New password</label>
        <PasswordInput testId="force-new-password" required minLength={6} value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">Confirm new password</label>
        <PasswordInput testId="force-confirm-password" required minLength={6} value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          className="w-full px-3.5 py-2.5 rounded-md bg-slate-950 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
        {error && <div data-testid="force-change-error" className="mt-4 text-sm rag-red px-3 py-2 rounded">{error}</div>}
        <button type="submit" disabled={busy} data-testid="force-change-submit"
          className="mt-6 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          {busy ? "Saving…" : "Set new password"}
        </button>
      </form>
    </div>
  );
}
